/**
 * TopBar — marque, statut machine/modèle, zone compte.
 * S'abonne au store ; la zone utilisateur est complétée par le workspace Compte.
 */
import { h } from "../components/dom.js";
import { serverGenerates } from "../services/execution.js";
import { type AppState, store } from "../state/store.js";

export class TopBar {
	readonly element: HTMLElement;
	private readonly dot: HTMLElement;
	private readonly statusText: HTMLElement;
	private readonly userZone: HTMLElement;

	constructor() {
		this.dot = h("span", { class: "status-dot" });
		this.statusText = h("span", null, "Connexion…");
		this.userZone = h("div", { class: "topbar-user" });
		this.element = h(
			"header",
			{ class: "app-topbar" },
			h("div", { class: "app-brand" }, "Image ", h("span", null, "Generator")),
			h("div", { class: "topbar-status" }, this.dot, this.statusText),
			h("div", { class: "topbar-spacer" }),
			this.userZone
		);

		// Une entrée du store par appel à subscribe() : chaque tranche qui
		// change la barre en demande un -- l'utilisateur compris : sans lui, la
		// barre gardait « Connectez-vous » à côté de l'adresse de la personne
		// connectée tant qu'aucune autre tranche ne bougeait.
		for (const slice of [
			(s: AppState) => s.user as unknown,
			(s: AppState) => s.status as unknown,
			(s: AppState) => s.selectedModel as unknown,
			(s: AppState) => s.workers as unknown,
			(s: AppState) => s.run as unknown,
		]) {
			store.subscribe(slice, () => this.render());
		}
		store.subscribe(
			(s) => s.user,
			(user) => {
				this.userZone.replaceChildren(
					user
						? h("span", { class: "topbar-email", title: user.email }, user.email)
						: h("span", null, "")
				);
			}
		);
		this.render();
	}

	/** Zone utilisateur (bouton compte / email) — pilotée par le workspace Compte. */
	get userElement(): HTMLElement {
		return this.userZone;
	}

	private render(): void {
		const state = store.get();
		this.dot.classList.remove("loading", "error");
		if (!state.status) {
			this.dot.classList.add("error");
			this.statusText.textContent = "Serveur indisponible";
			return;
		}
		// Sur le site public, /api/status décrit la VM qui sert la page : pas de
		// GPU, jamais de modèle chargé. La barre annonçait donc « aucun modèle ·
		// CPU » en permanence, y compris pendant une génération sur une RTX en
		// cours -- la machine de l'utilisateur, qui est la seule dont il veuille
		// des nouvelles. Ici on décrit cette machine-là.
		if (!serverGenerates()) {
			this.renderOwnMachine(state);
			return;
		}
		this.renderThisServer(state);
	}

	private renderOwnMachine(state: AppState): void {
		const modelName = state.selectedModel?.name ?? state.selectedModel?.id ?? null;
		if (state.run?.machineGone) {
			this.dot.classList.add("error");
			this.statusText.textContent = "Votre ordinateur s'est déconnecté";
			return;
		}
		if (state.run) {
			this.dot.classList.add("loading");
			const percent = state.run.percent;
			this.statusText.textContent =
				percent === null ? "Génération…" : `Génération… ${Math.round(percent)}%`;
			return;
		}
		const machine = state.workers.find((worker) => worker.online !== false);
		// Ce qui reste à faire, dit comme une étape : « aucun modèle » se lisait
		// « il n'y a pas de modèle » alors qu'il voulait dire « aucun choisi ».
		if (!machine) {
			this.dot.classList.add("error");
			this.statusText.textContent = state.user
				? "Reliez votre ordinateur (page Compte)"
				: "Connectez-vous pour commencer";
			return;
		}
		// Relié depuis la fenêtre, l'ordinateur est en ligne avant d'avoir son
		// moteur : la barre disait « est prêt » pendant les minutes où la fenêtre
		// l'installait. Ce que la fenêtre dit, la barre le redit -- seul : sur un
		// téléphone la barre coupe la fin, et c'est là qu'est le pourcentage.
		if (machine.engine_state === "installing") {
			this.dot.classList.add("loading");
			this.statusText.textContent =
				machine.engine_message ?? "Votre ordinateur prépare son moteur de calcul…";
			return;
		}
		if (machine.engine_state === "failed") {
			this.dot.classList.add("error");
			this.statusText.textContent =
				machine.engine_message ??
				"Le moteur de calcul n'a pas pu s'installer : ouvrez le programme pour réessayer.";
			return;
		}
		const computer = machine.gpu_name ?? "votre ordinateur";
		this.statusText.textContent = modelName
			? `${modelName} · ${computer}`
			: `${computer.charAt(0).toUpperCase()}${computer.slice(1)} est prêt · choisissez un modèle`;
	}

	private renderThisServer(state: AppState): void {
		const status = state.status as NonNullable<AppState["status"]>;
		// Le chargement d'abord : depuis la sélection paresseuse, il a lieu
		// pendant la génération, et « Chargement de FLUX… » dit plus que
		// « Génération… 0 % » — qui serait vrai, mais muet sur la demi-minute
		// qui passe avant la première étape.
		if (status.is_loading) {
			this.dot.classList.add("loading");
			this.statusText.textContent = status.loading_action ?? "Chargement du modèle…";
			return;
		}
		// Le même état que le bouton et le panneau de résultat, jamais un
		// second décompte tenu en parallèle.
		if (state.run) {
			this.dot.classList.add("loading");
			const percent = state.run.percent;
			this.statusText.textContent =
				percent === null ? "Génération…" : `Génération… ${Math.round(percent)} %`;
			return;
		}
		const modelName = status.model.name ?? status.model.id ?? "aucun modèle";
		const hw = status.hardware;
		const hwSuffix = hw?.gpu_name ? ` · ${hw.gpu_name}` : hw ? " · CPU" : "";
		this.statusText.textContent = `${modelName}${hwSuffix}`;
	}
}
